/**
 * useMermaidAsync — render the placeholders emitted by
 * `markdownConfig.ts` for diagram types that beautiful-mermaid v1.1.3
 * doesn't handle (erDiagram, sequenceDiagram, classDiagram, gantt, …).
 *
 * Scans the supplied root for `.mermaid-async[data-mermaid-source]`
 * nodes and replaces their content with an SVG produced by the official
 * `mermaid` package. Re-runs whenever the root or its innerHTML changes
 * (we observe via MutationObserver) so newly rendered markdown (e.g.
 * after a CollabReadView poll) also gets enhanced.
 *
 * mermaid.initialize is invoked once at module-load with a neutral
 * theme. Pass a different theme via `mermaidOptions` if needed.
 */

import { useEffect } from 'react'
import mermaid from 'mermaid'

let initialized = false
function ensureInitialized() {
  if (initialized) return
  mermaid.initialize({
    startOnLoad: false,
    theme: 'default',
    securityLevel: 'loose',
    fontFamily: 'Roboto, sans-serif',
  })
  initialized = true
}

/**
 * Attach to a DOM root once it mounts. The hook is idempotent — already-
 * rendered placeholders are skipped via `data-mermaid-rendered`.
 */
export function useMermaidAsync(root: HTMLElement | null): void {
  useEffect(() => {
    if (!root) return
    ensureInitialized()

    let cancelled = false
    let counter = 0
    const renderAll = async () => {
      if (cancelled) return
      const placeholders = root.querySelectorAll<HTMLElement>(
        '.mermaid-async:not([data-mermaid-rendered])',
      )
      for (const el of Array.from(placeholders)) {
        const source = el.getAttribute('data-mermaid-source') || ''
        if (!source.trim()) {
          el.setAttribute('data-mermaid-rendered', 'empty')
          continue
        }
        const id = `mermaid-async-${Date.now()}-${counter++}`
        try {
          const { svg } = await mermaid.render(id, source)
          if (cancelled) return
          el.innerHTML = svg
          el.setAttribute('data-mermaid-rendered', 'true')
        } catch (e) {
          if (cancelled) return
          el.innerHTML = `<pre class="mermaid-error"><code>${source
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')}</code></pre>`
          el.setAttribute('data-mermaid-rendered', 'error')
          // Silent — keep markdown viewable even if one diagram fails.
          // Surface via console for debugging.
          console.warn('mermaid render failed', e)
        }
      }
    }

    void renderAll()

    // Re-scan when the content changes (CollabReadView poll, edit/save
    // round-trip, etc.). MutationObserver fires on any DOM mutation in
    // the root subtree — debounced to a microtask.
    let scheduled = false
    const observer = new MutationObserver(() => {
      if (scheduled) return
      scheduled = true
      queueMicrotask(() => {
        scheduled = false
        void renderAll()
      })
    })
    observer.observe(root, { childList: true, subtree: true })

    return () => {
      cancelled = true
      observer.disconnect()
    }
  }, [root])
}
